import { useCallback, useEffect, useState } from "react";
import { RentalProfitTab } from "./RentalProfitTab";
import { TrendingUp, Car, CheckCircle2, KeyRound, Wrench, FileText, AlarmClock, Banknote, Users, CalendarDays } from "lucide-react";
import { RentalCalendarTab } from "./RentalCalendarTab";
import { api } from "../../lib/api";
import { RentalFleetTab } from "./RentalFleetTab";
import { RentalCustomersTab } from "./RentalCustomersTab";
import { RentalContractsTab } from "./RentalContractsTab";

const Kpi = ({ icon: Icon, label, value, tone, testid, onClick }) => (
  <div role="button" tabIndex={0} onClick={onClick} className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 transition hover:shadow-md" data-testid={testid}>
    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-xs text-slate-500">{label}</p><p className="font-heading text-xl font-bold tabular-nums text-slate-800">{value}</p></div>
  </div>
);

const TABS = [
  { id: "contracts", label: "العقود", Icon: FileText },
  { id: "fleet", label: "مركبات التأجير", Icon: Car },
  { id: "calendar", label: "التقويم", Icon: CalendarDays },
  { id: "customers", label: "العملاء", Icon: Users },
  { id: "profit", label: "الأرباح", Icon: TrendingUp },
];

export default function RentalsPanel({ canEdit, employees = [] }) {
  const [tab, setTab] = useState("contracts");
  const [preset, setPreset] = useState({ n: 0 });
  const go = (t, p) => { setTab(t); setPreset((x) => ({ ...p, n: x.n + 1 })); };
  const [sum, setSum] = useState({});
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => { api.rentalSummary().then(setSum).catch(() => {}); }, [tick]);

  return (
    <div className="space-y-4" dir="rtl" data-testid="rentals-panel">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi icon={Car} label="مركبات التأجير" value={sum.fleet ?? 0} tone="bg-slate-100 text-slate-600" testid="rental-kpi-fleet" onClick={() => go("fleet", { fleet: "" })} />
        <Kpi icon={CheckCircle2} label="متاحة" value={sum.available ?? 0} tone="bg-emerald-50 text-emerald-600" testid="rental-kpi-available" onClick={() => go("fleet", { fleet: "available" })} />
        <Kpi icon={KeyRound} label="مؤجرة" value={sum.rented ?? 0} tone="bg-blue-50 text-blue-600" testid="rental-kpi-rented" onClick={() => go("fleet", { fleet: "rented" })} />
        <Kpi icon={Wrench} label="صيانة" value={sum.maintenance ?? 0} tone="bg-amber-50 text-amber-600" testid="rental-kpi-maintenance" onClick={() => go("fleet", { fleet: "maintenance" })} />
        <Kpi icon={AlarmClock} label="عقود متأخرة" value={sum.overdue ?? 0} tone="bg-rose-50 text-rose-600" testid="rental-kpi-overdue" onClick={() => go("contracts", { contracts: "overdue" })} />
        <Kpi icon={Banknote} label="إيرادات الشهر" value={Number(sum.month_revenue || 0).toLocaleString("en-US")} tone="bg-violet-50 text-violet-600" testid="rental-kpi-revenue" onClick={() => go("contracts", { contracts: "all" })} />
      </div>
      <div className="flex gap-1.5 rounded-xl bg-slate-100 p-1 w-fit max-w-full overflow-x-auto">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`rental-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === id ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "contracts" && <RentalContractsTab key={`c${preset.n}`} preset={preset.contracts} canEdit={canEdit} employees={employees} onChanged={refresh} />}
      {tab === "fleet" && <RentalFleetTab key={`f${preset.n}`} preset={preset.fleet} canEdit={canEdit} onChanged={refresh} />}
      {tab === "calendar" && <RentalCalendarTab />}
      {tab === "customers" && <RentalCustomersTab canEdit={canEdit} />}
      {tab === "profit" && <RentalProfitTab />}
    </div>
  );
}
